// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the "Konvo Assistant" entries of a message's right-click menu.
// Each one either asks the assistant for a single suggestion about that
// message and opens the matching prefilled form, or hands the message to the
// Assistant DM as a prompt.

import type {Channel} from '@app/features/channel/models/Channel';
import AssistantSuggestions, {type AssistantSuggestion} from '@app/features/konvo_assistant/state/AssistantSuggestions';
import {openQuickEvent, type QuickEventPrefill} from '@app/features/konvo_calendar/components/KonvoQuickEventCard';
import {
	openKonvoAssistant,
	sendToKonvoAssistant,
} from '@app/features/konvo_connections/commands/KonvoAssistantCommands';
import {KonvoRemindMeModal} from '@app/features/konvo_reminders/components/KonvoRemindMeModal';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import KonvoClickUp from '@app/features/messaging/state/KonvoClickUp';
import Messages from '@app/features/messaging/state/MessagingMessages';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import Users from '@app/features/user/state/Users';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const THINKING_DESCRIPTOR = msg({
	message: 'Asking your assistant…',
	comment: 'Toast while the assistant reads a message for the right-click flow.',
});
const NO_SUGGESTION_DESCRIPTOR = msg({
	message: 'Your assistant could not make a suggestion from that message.',
	comment: 'Toast when the right-click assistant flow returns nothing.',
});
const REMIND_REQUEST_DESCRIPTOR = msg({
	message: 'Suggest when to remind me about this message: "{text}"',
	comment:
		'Request shown in the Konvo panel when the reminder suggestion is used before the assistant is connected. {text} is the start of the message.',
});
const HANDED_OVER_DESCRIPTOR = msg({
	message: 'Your Assistant is drafting this task. The card will arrive in your Assistant chat.',
	comment: 'Toast after handing a suggested task to the Assistant DM from the suggestion strip.',
});
const HANDOVER_FAILED_DESCRIPTOR = msg({
	message: 'Could not reach your Assistant. Try again.',
	comment: 'Toast when handing a suggested task to the Assistant DM fails.',
});
const SUMMARY_MAX_CHARS = 3500;
const HANDOVER_BEFORE = 12;
const HANDOVER_AFTER = 4;

const snippet = (message: Message, max = 600): string => message.content.replace(/\s+/g, ' ').trim().slice(0, max);
const authorName = (message: Message): string => message.author.globalName ?? message.author.username;

function nextHalfHour(): Date {
	const date = new Date(Date.now() + 30 * 60 * 1000);
	date.setMinutes(date.getMinutes() < 30 ? 30 : 60, 0, 0);
	return date;
}

/** Opens the quick event card for a message; the assistant fills in time, title and people. */
export async function createEventFromMessage(
	i18n: I18n,
	message: Message,
	channel: Channel | null,
	target?: HTMLElement | null,
): Promise<void> {
	if (!channel) return;
	if (!AssistantSuggestions.available) {
		// No assistant: still useful — open the card with sensible defaults.
		openQuickEvent({prefill: fallbackPrefill(message), channel, target});
		return;
	}
	ToastCommands.createToast({type: 'info', children: i18n._(THINKING_DESCRIPTOR)});
	const messages = Messages.getMessages(channel.id).toArray();
	const {suggestion, people} = await AssistantSuggestions.requestFor(
		channel,
		messages,
		message,
		'event',
		Users.currentUser?.id ?? null,
	);
	const prefill: QuickEventPrefill =
		suggestion?.kind === 'event'
			? {
					subject: suggestion.title,
					start: suggestion.start,
					end: suggestion.end,
					attendees: suggestion.attendees,
					people: people.length > 0 ? people : suggestion.attendees,
					hint: suggestion.summary,
				}
			: {...fallbackPrefill(message), people};
	openQuickEvent({prefill, channel, target});
}

function fallbackPrefill(message: Message): QuickEventPrefill {
	const start = nextHalfHour();
	return {
		subject: snippet(message, 80),
		start: start.toISOString(),
		end: new Date(start.getTime() + 30 * 60 * 1000).toISOString(),
		attendees: [],
		people: [],
		hint: null,
	};
}

/**
 * Asks the assistant when to be reminded about a message, then opens the remind-me form prefilled.
 * Readiness is the Konvo panel's rule (the selected assistant is linked and on). Without it
 * the panel's connect step opens and, once connected, runs this again (`fromPanel`, so it
 * never loops back into the panel).
 */
export async function suggestReminderFromMessage(
	i18n: I18n,
	message: Message,
	channel: Channel | null,
	fromPanel = false,
): Promise<void> {
	if (!channel) return;
	if (!fromPanel && !AssistantSuggestions.available) {
		// Loaded on demand so this module stays light (its tests run without the app runtime).
		const {default: KonvoPanel} = await import('@app/features/konvo_panel/state/KonvoPanel');
		KonvoPanel.open({
			starter: 'free',
			request: i18n._(REMIND_REQUEST_DESCRIPTOR, {text: snippet(message, 120)}),
			source: 'starter',
			page: 'chats',
			run: false,
			perform: () => void suggestReminderFromMessage(i18n, message, channel, true),
		});
		return;
	}
	ToastCommands.createToast({type: 'info', children: i18n._(THINKING_DESCRIPTOR)});
	const messages = Messages.getMessages(channel.id).toArray();
	const {suggestion} = await AssistantSuggestions.requestFor(
		channel,
		messages,
		message,
		'reminder',
		Users.currentUser?.id ?? null,
	);
	const initial = reminderInitial(suggestion);
	if (!initial) ToastCommands.createToast({type: 'error', children: i18n._(NO_SUGGESTION_DESCRIPTOR)});
	ModalCommands.push(
		modal(() => (
			<KonvoRemindMeModal message={message} initial={initial ?? undefined} onClose={() => ModalCommands.pop()} />
		)),
	);
}

function reminderInitial(suggestion: AssistantSuggestion | null): {remindAt: Date; note?: string} | null {
	if (suggestion?.kind !== 'reminder') return null;
	const remindAt = new Date(suggestion.remind_at);
	return Number.isNaN(remindAt.getTime()) ? null : {remindAt, note: suggestion.note};
}

/** Sends the message to the Assistant DM with a question prompt. */
export async function askKonvoAboutMessage(message: Message): Promise<void> {
	const text = snippet(message, 1500);
	await openKonvoAssistant(
		`${authorName(message)} wrote in chat:\n> ${text.replace(/\n/g, '\n> ')}\n\nWhat should I know or do about this? Be brief.`,
	);
}

/** Sends the recent conversation to the Assistant DM asking for a summary. */
export async function summariseThread(channel: Channel): Promise<void> {
	const messages = Messages.getMessages(channel.id)
		.toArray()
		.filter((entry) => entry.isUserMessage() && entry.content.trim().length > 0);
	const lines: Array<string> = [];
	let total = 0;
	for (let index = messages.length - 1; index >= 0; index -= 1) {
		const entry = messages[index];
		const line = `${authorName(entry)}: ${snippet(entry, 300)}`;
		if (total + line.length > SUMMARY_MAX_CHARS) break;
		lines.unshift(line);
		total += line.length + 1;
	}
	if (lines.length === 0) return;
	await openKonvoAssistant(
		`Summarise this recent conversation from ${channel.name ? `#${channel.name}` : 'a chat'} in a few bullets, then list any decisions, open questions and things I need to do:\n\n${lines.join('\n')}`,
	);
}

/**
 * Hands a suggested task to the Assistant DM for the thorough version: it reads
 * the conversation, checks for an existing task, picks the list and people and
 * opens the review card there, while the reader keeps working here.
 */
export async function draftTaskInAssistant(
	i18n: I18n,
	channel: Channel,
	message: Message,
	idea: {name: string; summary: string; listId?: string | null} | null,
): Promise<boolean> {
	// A screenshot with no caption still counts: it is often the whole point of the task.
	const messages = Messages.getMessages(channel.id)
		.toArray()
		.filter((entry) => entry.isUserMessage() && (entry.content.trim().length > 0 || entry.attachments.length > 0));
	const index = messages.findIndex((entry) => entry.id === message.id);
	const around =
		index === -1 ? [message] : messages.slice(Math.max(0, index - HANDOVER_BEFORE), index + HANDOVER_AFTER + 1);
	const lines: Array<string> = [];
	let total = 0;
	for (const entry of around) {
		const files = entry.attachments
			.slice(0, 5)
			.map((file) => `${file.filename} (file id f_${channel.id}_${entry.id}_${file.id})`);
		const said = [snippet(entry, 400), files.length > 0 ? `[files: ${files.join(', ')}]` : '']
			.filter(Boolean)
			.join(' ');
		const line = `${entry.id === message.id ? '>> ' : ''}${authorName(entry)}: ${said}`;
		if (total + line.length > SUMMARY_MAX_CHARS) break;
		lines.push(line);
		total += line.length + 1;
	}
	const where = channel.name ? `#${channel.name}` : 'a chat';
	const list = idea?.listId ? KonvoClickUp.lists.find((entry) => entry.id === idea.listId) : undefined;
	const prompt = [
		`Draft a ClickUp task from this conversation in ${where} (channel_id=${channel.id}; message_id=${message.id}). The line marked >> is the one it came from.`,
		'',
		lines.join('\n'),
		'',
		idea ? `First idea: ${idea.name}. ${idea.summary}` : null,
		list ? `Likely list: ${list.path || list.name} (list id ${list.id}).` : null,
		`Link the source in the description as konvo://channels/${channel.guildId ?? '@me'}/${channel.id}/${message.id}.`,
		'Work out what the task should say, the right list (pass it to create_task) and who should do it. Set a due date only if the conversation gives one. Check whether a matching task already exists (then offer to update it instead), attach the files from the conversation that belong with it (by their file ids), and open the task card for me to review.',
	]
		.filter((line): line is string => line != null)
		.join('\n');
	try {
		await sendToKonvoAssistant(prompt);
		ToastCommands.createToast({type: 'success', children: i18n._(HANDED_OVER_DESCRIPTOR)});
		return true;
	} catch {
		ToastCommands.createToast({type: 'error', children: i18n._(HANDOVER_FAILED_DESCRIPTOR)});
		return false;
	}
}
